package com.heelion.farmafreelance.agent

import kotlinx.datetime.LocalDate
import com.heelion.farmafreelance.voice.VoiceCommandParser
import com.heelion.farmafreelance.voice.VoiceIntent

/** Domande sui dati dell'utente a cui l'agente risponde (vedi [RisponditoreDomande]). */
enum class TipoDomanda(
    /** true se per rispondere servono i turni aperti delle controparti (chiamata di rete). */
    val richiedeAnnunci: Boolean = false,
    /** true se serve lo storico dei turni svolti (chiamata di rete). */
    val richiedeStorico: Boolean = false,
    /** true se servono i turni (o le disponibilità) pubblicati dall'utente stesso (chiamata di rete). */
    val richiedeMieiAnnunci: Boolean = false
) {
    PROSSIMO_TURNO, STATO_TURNI, NOTIFICHE, ABBONAMENTO, REPUTAZIONE, AIUTO,
    TURNI_DISPONIBILI(richiedeAnnunci = true),
    TURNI_PREFERITI(richiedeAnnunci = true),
    RIEPILOGO,
    /** Quali promemoria di Farmy sono attivi. */
    PROMEMORIA_ATTIVI,
    /** Guadagni del farmacista nel periodo detto nella domanda (lordo, ENPAF). */
    GUADAGNI(richiedeStorico = true),
    /** Agenda dei pagamenti del farmacista: chi deve ancora pagarlo (vedi [ResocontoPagamenti]). */
    PAGAMENTI(richiedeStorico = true),
    /** "Quali farmacie mi hanno pagato di più?": classifica dallo storico (vedi [ClassificaFarmacie]). */
    FARMACIE_PIU_PAGANTI(richiedeStorico = true),
    /** "Chi paga di più nella mia zona?": turni aperti per compenso orario (vedi [TurniMeglioPagati]). */
    TURNI_PIU_PAGATI(richiedeAnnunci = true),
    /** "Ci sono emergenze vicino a me?": turni entro 24 ore, i più vicini prima (vedi [EmergenzeFarmy]). */
    EMERGENZE(richiedeAnnunci = true),
    /** "Turni nelle farmacie vicino a me": turni aperti dalla farmacia più vicina (vedi [TurniVicini]). */
    TURNI_VICINI(richiedeAnnunci = true),
    /** Riepilogo dei turni da fare e di quelli svolti (vedi [AgendaTurni]). */
    AGENDA(richiedeStorico = true),
    /** "Il turno di sabato è coperto?": chi copre i turni pubblicati (vedi [DomandeCalendario]). */
    TURNO_COPERTO(richiedeMieiAnnunci = true),
    /** "Quali turni ho pubblicato?": i propri turni o disponibilità ancora aperti. */
    TURNI_PUBBLICATI(richiedeMieiAnnunci = true),
    /** "Sono libero sabato?": gli impegni di quel giorno o periodo. */
    SONO_LIBERO
}

/** "Pubblica un turno/una disponibilità per sabato dalle 8 alle 20" -> la creazione di quel turno. */
internal object CreazioneDaPubblica {
    private val PUBBLICA = Regex("^(pubblica|pubblicami|metti|mettimi)( una| un)? (disponibilita|turno|turni|annuncio)\\b")

    fun intent(testo: String, oggi: LocalDate): VoiceIntent.CreaTurno? {
        val t = IntentClassifier.normalizza(testo)
        val m = PUBBLICA.find(t) ?: return null
        val crea = VoiceCommandParser.parse("crea turno " + t.substring(m.range.last + 1), oggi) as? VoiceIntent.CreaTurno ?: return null
        // Senza giorno non c'è niente da creare: meglio non indovinare.
        return crea.takeIf { it.data != null || it.dateEsplicite != null || it.giorniSettimana.isNotEmpty() }
    }
}

sealed class RispostaAgente {
    /** Un'azione da eseguire nell'app. [daModello] = capita dal modello, non dalle regole. */
    data class Comando(val intent: VoiceIntent, val daModello: Boolean) : RispostaAgente()

    /** Una domanda: la risposta si costruisce dai dati dell'utente con [RisponditoreDomande]. */
    data class Domanda(val tipo: TipoDomanda) : RispostaAgente()

    /** Attivare/disattivare promemoria ("ricordami ogni mattina i turni della giornata"). */
    data class ImpostaPromemoria(val impostazione: ImpostazionePromemoria) : RispostaAgente()

    /** Un'azione nell'app (rispondere a una proposta, preferiti...): Farmy chiede un "sì" prima di eseguirla. */
    data class Azione(val azione: AzioneFarmy) : RispostaAgente()

    data class NonCapito(val testo: String) : RispostaAgente()

    /** Saluti, grazie, "non so cosa chiederti": Farmy risponde come un amico (vedi [ConversazioneFarmy]). */
    data class Chiacchiera(val tipo: TipoChiacchiera) : RispostaAgente()
}

/**
 * L'agente di FarmaFreelance: combina il classificatore addestrato in Python ([IntentClassifier])
 * con il parser a regole esistente ([VoiceCommandParser]). Tutto locale, gratuito, offline.
 *
 * Ordine delle decisioni:
 * 1. Domande ("quando è il mio prossimo turno?"): le riconosce solo il modello (il parser non
 *    ha domande), quindi vengono valutate per prime, se il modello è sicuro.
 * 2. Comandi: se modello (sicuro) e parser sono d'accordo sul tipo di azione, o il modello non
 *    è sicuro, vale il parser. Per contraddire un comando che il parser ha capito serve una
 *    sicurezza più alta ([SOGLIA_CORREZIONE]).
 * 3. Se sono in disaccordo, o il parser non capisce, si ri-analizza la frase con il verbo
 *    canonico dell'azione scelta dal modello davanti ("levami il turno di domani" ->
 *    "cancella levami il turno di domani"): così date, orari, compensi e zone li estrae sempre
 *    il parser, con tutti i suoi controlli (es. niente cancellazione senza un criterio di
 *    data). Il risultato è accettato solo se il parser conferma lo stesso tipo di azione.
 *    Serve anche a correggere il parser, che con verbi sconosciuti sbaglia in modo pericoloso:
 *    da solo legge "annulla il turno di mercoledì" come una CREAZIONE (vede "turno" + data).
 * 4. Se il modello è sicuro che non sia un comando dell'app, il comando del parser NON parte:
 *    il parser cerca sottostringhe e "mettimi della musica" per lui è una conferma ("mu-si-ca").
 *
 * Senza modello (non ancora caricato, o caricamento fallito) si comporta esattamente come
 * il solo parser a regole.
 */
class FarmaFreelanceAgent(
    private val classificatore: IntentClassifier? = null,
    private val soglia: Double = SOGLIA_DEFAULT
) : CommandAgent {

    fun elabora(frase: String, oggi: LocalDate): RispostaAgente {
        // Parole singole troppo corte per il modello (pochi n-grammi = probabilità dispersa tra
        // tanti intenti): riconosciute in modo esatto, prima di tutto il resto.
        FRASI_ESATTE[IntentClassifier.normalizza(frase)]?.let { return RispostaAgente.Domanda(it) }
        // "ciao", "grazie", "chi sei?", "non so cosa chiederti": conversazione, non un comando.
        ConversazioneFarmy.tipo(frase)?.let { return RispostaAgente.Chiacchiera(it) }
        // "ciao Farmy, quando lavoro? grazie" -> "quando lavoro?"
        val text = ConversazioneFarmy.pulisci(frase)
        FRASI_ESATTE[IntentClassifier.normalizza(text)]?.let { return RispostaAgente.Domanda(it) }

        // "pubblica una disponibilità per sabato dalle 8 alle 20": è una creazione con i suoi dati, non
        // il "pubblica" che conferma il turno già pronto nella pagina (quello resta una parola sola).
        CreazioneDaPubblica.intent(text, oggi)?.let { return RispostaAgente.Comando(it, daModello = false) }
        // "conferma il turno di sabato": regola esplicita, il parser la leggerebbe come creazione.
        if (InterpreteAzioni.eRispostaProposta(text)) {
            return RispostaAgente.Azione(AzioneFarmy.RispondiProposta(InterpreteAzioni.azioneProposta(text), text))
        }
        // "cerco farmacista galenico a 35 € l'ora di sabato": ricerca con criteri (competenza, tariffa,
        // giorno) risolta da Farmy; senza criteri resta la normale ricerca farmacisti per zona.
        if (RicercaFarmacisti.eRicercaFarmy(text, oggi)) return RispostaAgente.Azione(AzioneFarmy.TrovaFarmacista(text))
        // "avvisami quando c'è un turno a Roma il sabato": ricerca salvata (non i promemoria dei propri turni).
        if (AvvisiFarmy.eRichiestaAvviso(text)) return RispostaAgente.Azione(AzioneFarmy.SalvaAvviso(text))
        // "prendi il turno urgente della Farmacia Rossi": accettare un turno aperto (non una proposta).
        if (InterpreteAzioni.ePrendiTurno(text)) return RispostaAgente.Azione(AzioneFarmy.PrendiTurno(text))
        // "quali farmacie mi hanno pagato di più?", "emergenze vicino a me?", "riepilogo dei miei turni":
        // regole, prima dei pagamenti ("...mi hanno pagato" lì varrebbe come "chi mi deve pagare").
        DomandeTurni.tipo(text)?.let { return RispostaAgente.Domanda(it) }
        // "il turno di sabato è coperto?", "sono libero sabato?", "ci sono turni nel weekend?": regole.
        DomandeCalendario.tipo(text, oggi)?.let { return RispostaAgente.Domanda(it) }
        // "apri l'agenda": pagina che il modello non conosce (potrebbe scambiarla per un profilo da aprire).
        (VoiceCommandParser.parse(text, oggi) as? VoiceIntent.NavigaA)?.takeIf { it.sezione == VoiceIntent.Sezione.AGENDA }
            ?.let { return RispostaAgente.Comando(it, daModello = false) }
        // "chi mi deve ancora pagare?": regola (il modello non ha questo intento).
        if (ResocontoPagamenti.eDomandaPagamenti(text)) return RispostaAgente.Domanda(TipoDomanda.PAGAMENTI)

        val c = classificatore?.classifica(text)?.takeIf { it.confidenza >= soglia }

        // "vai ai preferiti", "apri le notifiche": verbo di navigazione + pagina riconosciuti dal
        // parser sono inequivocabili e vincono anche su una domanda simile ("ci sono turni dei
        // preferiti?", "ho notifiche?"), altrimenti Farmy risponderebbe invece di aprire la pagina.
        // "apri il profilo della farmacia Rossi" apre quel profilo; senza nome è il proprio (pagina Profilo).
        if (c?.intento == "APRI_PROFILO") {
            val nome = InterpreteAzioni.nomeCercato(text)
            return if (nome.isNotBlank()) RispostaAgente.Azione(AzioneFarmy.ApriProfilo(nome))
            else RispostaAgente.Comando(VoiceIntent.NavigaA(VoiceIntent.Sezione.PROFILO), daModello = true)
        }

        val regoleNavigazione = VoiceCommandParser.parse(text, oggi)
        if (regoleNavigazione is VoiceIntent.NavigaA) return RispostaAgente.Comando(regoleNavigazione, daModello = false)

        DOMANDE[c?.intento]?.let { return RispostaAgente.Domanda(it) }
        if (c?.intento == "PROMEMORIA_IMPOSTA") return RispostaAgente.ImpostaPromemoria(EstrattorePromemoria.estrai(text))
        when (c?.intento) {
            "RISPONDI_PROPOSTA" ->
                return RispostaAgente.Azione(AzioneFarmy.RispondiProposta(InterpreteAzioni.azioneProposta(text), text))
            "PROPOSTA_DIRETTA" -> return RispostaAgente.Azione(AzioneFarmy.ProponiTurno(InterpreteAzioni.nomeCercato(text)))
            "PRENDI_TURNO" -> return RispostaAgente.Azione(AzioneFarmy.PrendiTurno(text))
            "PREFERITI" -> return RispostaAgente.Azione(
                AzioneFarmy.Preferito(InterpreteAzioni.nomeCercato(text), InterpreteAzioni.preferitoAggiungi(text))
            )
        }

        val regole = VoiceCommandParser.parse(text, oggi)
        val regoleCapite = regole !is VoiceIntent.Unrecognized
        // Contraddire un comando che il parser HA capito richiede più sicurezza che riempire un
        // suo vuoto: es. "turno 15/03 compenso 30 euro" è una creazione, ma "compenso" fa
        // pensare al modello (65%) a una modifica.
        val puoCorreggere = c != null && (!regoleCapite || c.confidenza >= SOGLIA_CORREZIONE)
        if (c == null || corrisponde(c.intento, regole) || !puoCorreggere) {
            return if (regoleCapite) RispostaAgente.Comando(regole, daModello = false) else RispostaAgente.NonCapito(frase)
        }

        // Disaccordo (o il parser non ha capito): si prova l'azione indicata dal modello.
        val prefisso = GUIDE[c.intento]
        if (prefisso != null) {
            val guidato = VoiceCommandParser.parse("$prefisso $text", oggi)
            if (corrisponde(c.intento, guidato) && !ricercaDegenere(guidato, text)) {
                return RispostaAgente.Comando(guidato, daModello = true)
            }
        }
        if (c.intento == NON_COMANDO) return RispostaAgente.NonCapito(frase)
        return if (regoleCapite) RispostaAgente.Comando(regole, daModello = false) else RispostaAgente.NonCapito(frase)
    }

    /**
     * Una ricerca guidata in cui il parser ha preso come comune/nome l'INTERA frase ("cos'è un
     * farmacista" -> comune "Cos è un farmacista"): non è una ricerca vera, meglio "non ho capito".
     */
    private fun ricercaDegenere(intent: VoiceIntent, text: String): Boolean {
        val frase = IntentClassifier.normalizza(text)
        val campi = when (intent) {
            is VoiceIntent.CercaFarmacisti -> listOf(intent.comune, intent.query)
            is VoiceIntent.CercaFarmacie -> listOf(intent.comune, intent.query)
            else -> return false
        }
        return campi.any { it != null && IntentClassifier.normalizza(it) == frase }
    }

    /**
     * Cosa poteva voler dire [text] secondo il modello, anche sotto la soglia per agire: le etichette
     * più probabili (al massimo [quante]), per proporre all'utente le domande giuste quando non ha capito.
     */
    fun ipotesi(text: String, quante: Int = 3): List<String> {
        val c = classificatore ?: return emptyList()
        val p = c.probabilita(ConversazioneFarmy.pulisci(text))
        return c.intenti.indices.sortedByDescending { p[it] }
            .filter { p[it] >= SOGLIA_IPOTESI && c.intenti[it] != NON_COMANDO }
            .take(quante).map { c.intenti[it] }
    }

    override fun interpret(text: String, oggi: LocalDate): VoiceIntent =
        (elabora(text, oggi) as? RispostaAgente.Comando)?.intent ?: VoiceIntent.Unrecognized(text)

    companion object {
        const val SOGLIA_DEFAULT = 0.6
        const val SOGLIA_CORREZIONE = 0.8
        /** Sotto questa probabilità un'ipotesi è solo rumore: non la si propone. */
        const val SOGLIA_IPOTESI = 0.1

        private val FRASI_ESATTE = mapOf(
            "aiuto" to TipoDomanda.AIUTO,
            "help" to TipoDomanda.AIUTO,
            "cosa sai fare" to TipoDomanda.AIUTO
        )
        private const val NON_COMANDO = "UNRECOGNIZED"

        /** true se [intent] (del parser) è lo stesso tipo di azione dell'etichetta del modello. */
        internal fun corrisponde(etichetta: String, intent: VoiceIntent): Boolean = when (etichetta) {
            "CREA_TURNO" -> intent is VoiceIntent.CreaTurno
            "CERCA_FARMACIE" -> intent is VoiceIntent.CercaFarmacie
            "CERCA_FARMACISTI" -> intent is VoiceIntent.CercaFarmacisti
            "MOSTRA_TURNI_FARMACIA" -> intent is VoiceIntent.MostraTurniFarmacia
            "FILTRA_TURNI_STATO" -> intent is VoiceIntent.FiltraTurniStato
            "NAVIGA_A" -> intent is VoiceIntent.NavigaA
            "APRI_MAPPA" -> intent is VoiceIntent.ApriMappa
            "CONFERMA" -> intent is VoiceIntent.Conferma
            "GESTISCI_ANNUNCI_CANCELLA" ->
                intent is VoiceIntent.GestisciAnnunci && intent.azione == VoiceIntent.AzioneAnnunci.CANCELLA
            "GESTISCI_ANNUNCI_MODIFICA" ->
                intent is VoiceIntent.GestisciAnnunci && intent.azione == VoiceIntent.AzioneAnnunci.MODIFICA
            NON_COMANDO -> intent is VoiceIntent.Unrecognized
            else -> false
        }

        private val DOMANDE = mapOf(
            "DOMANDA_PROSSIMO_TURNO" to TipoDomanda.PROSSIMO_TURNO,
            "DOMANDA_STATO_TURNI" to TipoDomanda.STATO_TURNI,
            "DOMANDA_NOTIFICHE" to TipoDomanda.NOTIFICHE,
            "DOMANDA_ABBONAMENTO" to TipoDomanda.ABBONAMENTO,
            "DOMANDA_REPUTAZIONE" to TipoDomanda.REPUTAZIONE,
            "AIUTO" to TipoDomanda.AIUTO,
            "DOMANDA_TURNI_DISPONIBILI" to TipoDomanda.TURNI_DISPONIBILI,
            "DOMANDA_TURNI_PREFERITI" to TipoDomanda.TURNI_PREFERITI,
            "DOMANDA_RIEPILOGO" to TipoDomanda.RIEPILOGO,
            "DOMANDA_PROMEMORIA" to TipoDomanda.PROMEMORIA_ATTIVI,
            "DOMANDA_GUADAGNI" to TipoDomanda.GUADAGNI,
            "DOMANDA_FARMACIE_PAGANTI" to TipoDomanda.FARMACIE_PIU_PAGANTI,
            "DOMANDA_TURNI_PAGATI" to TipoDomanda.TURNI_PIU_PAGATI,
            "DOMANDA_EMERGENZE" to TipoDomanda.EMERGENZE,
            "DOMANDA_AGENDA" to TipoDomanda.AGENDA
        )

        // Verbo canonico da anteporre alla frase per far estrarre i campi al parser.
        // Volutamente assenti:
        // - CONFERMA: esegue l'azione in sospeso (anche una cancellazione), quindi deve sempre
        //   partire da una parola esplicita riconosciuta dalle regole, mai da una stima.
        // - FILTRA_TURNI_STATO: senza una parola di stato il parser non ha nulla da filtrare.
        private val GUIDE = mapOf(
            "CREA_TURNO" to "crea turno",
            "CERCA_FARMACIE" to "cerca farmacie",
            "CERCA_FARMACISTI" to "cerca farmacisti",
            "MOSTRA_TURNI_FARMACIA" to "mostrami i turni della farmacia",
            "NAVIGA_A" to "vai",
            "APRI_MAPPA" to "apri maps",
            "GESTISCI_ANNUNCI_CANCELLA" to "cancella",
            "GESTISCI_ANNUNCI_MODIFICA" to "modifica"
        )
    }
}
